import React, { useState } from "react";
import { submitContactForm } from "../../services/contactAPI";
import {
  validateContactForm,
  validateField,
} from "../../utils/contactValidation";
import "./styles/Contact.css";

const initialState = {
  first_name: "",
  last_name: "",
  email: "",
  company_name: "",
  job_title: "",
  phone_number: "",
  contact_method: "",
  area_of_interest: "",
  additional_comments: "",
};

const contactHighlights = [
  "Share your goals, challenges, or project scope",
  "Connect with the right team quickly",
  "Get practical guidance on cloud, infrastructure, and security",
];

export default function Contact() {
  const [form, setForm] = useState(initialState);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (fieldErrors[name]) {
      setFieldErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const errorMessage = validateField(name, value);

    if (errorMessage) {
      setFieldErrors((prev) => ({
        ...prev,
        [name]: errorMessage,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess(false);

    const validation = validateContactForm(form);

    if (!validation.isValid) {
      setFieldErrors(validation.errors);
      setError("Please review the highlighted fields before submitting.");
      return;
    }

    setLoading(true);

    try {
      await submitContactForm(form);
      setSuccess(true);
      setForm(initialState);
      setFieldErrors({});
    } catch (err) {
      if (err.errors) {
        setFieldErrors(err.errors);
        setError("Please fix the errors and try again.");
      } else {
        setError(err.error || "Submission failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="contactPage">
      <section className="contactHero">
        <div className="contactContainer contactHero__container">
          <div className="contactHero__content">
            <p className="contactHero__kicker">Contact Com Networks</p>

            <h1 className="contactHero__title">
              Start the conversation with the right team.
            </h1>

            <p className="contactHero__text">
              If you are evaluating infrastructure improvements, strengthening
              cybersecurity, or planning cloud initiatives, we are here to help
              you move forward with clear guidance and the right technical
              support.
            </p>

            <div className="contactHero__highlights">
              {contactHighlights.map((item) => (
                <div key={item} className="contactHero__highlight">
                  <span className="contactHero__dot" />
                  <span>{item}</span>
                </div>
              ))}
            </div>

            <div className="contactInfoCard">
              <h2 className="contactInfoCard__title">Direct contact</h2>

              <div className="contactInfoCard__list">
                <div className="contactInfoCard__item">
                  <span className="contactInfoCard__label">Email</span>
                  <a href="mailto:info@algdataguard.com">
                    Info@ComNetworks.net
                  </a>
                </div>

                <div className="contactInfoCard__item">
                  <span className="contactInfoCard__label">Phone</span>
                  <a href="tel:+17543038641">+1 754-303-8641</a>
                </div>

                <div className="contactInfoCard__item">
                  <span className="contactInfoCard__label">What we support</span>
                  <p>
                    Networking, cloud, cybersecurity, infrastructure, and
                    strategic IT consulting.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="contactFormShell">
            <div className="contactFormShell__header">
              <p className="contactFormShell__eyebrow">Send an inquiry</p>
              <h2 className="contactFormShell__title">Tell us what you need</h2>
              <p className="contactFormShell__text">
                Fill out the form below and we will connect you with the right
                person as quickly as possible.
              </p>
            </div>

            <form className="contactForm" onSubmit={handleSubmit} noValidate>
              <div className="contactField">
                <label htmlFor="first_name">First name</label>
                <input
                  id="first_name"
                  type="text"
                  name="first_name"
                  value={form.first_name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={fieldErrors.first_name ? "error" : ""}
                  placeholder="Enter your first name"
                />
                {fieldErrors.first_name && (
                  <span className="field-error">{fieldErrors.first_name}</span>
                )}
              </div>

              <div className="contactField">
                <label htmlFor="last_name">Last name</label>
                <input
                  id="last_name"
                  type="text"
                  name="last_name"
                  value={form.last_name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={fieldErrors.last_name ? "error" : ""}
                  placeholder="Enter your last name"
                />
                {fieldErrors.last_name && (
                  <span className="field-error">{fieldErrors.last_name}</span>
                )}
              </div>

              <div className="contactField contactField--full">
                <label htmlFor="email">Work email</label>
                <input
                  id="email"
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={fieldErrors.email ? "error" : ""}
                  placeholder="name@company.com"
                />
                {fieldErrors.email && (
                  <span className="field-error">{fieldErrors.email}</span>
                )}
              </div>

              <div className="contactField">
                <label htmlFor="company_name">Company name</label>
                <input
                  id="company_name"
                  type="text"
                  name="company_name"
                  value={form.company_name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={fieldErrors.company_name ? "error" : ""}
                  placeholder="Your organization"
                />
                {fieldErrors.company_name && (
                  <span className="field-error">{fieldErrors.company_name}</span>
                )}
              </div>

              <div className="contactField">
                <label htmlFor="job_title">Job title</label>
                <input
                  id="job_title"
                  type="text"
                  name="job_title"
                  value={form.job_title}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={fieldErrors.job_title ? "error" : ""}
                  placeholder="Your role"
                />
                {fieldErrors.job_title && (
                  <span className="field-error">{fieldErrors.job_title}</span>
                )}
              </div>

              <div className="contactField">
                <label htmlFor="phone_number">Phone number</label>
                <input
                  id="phone_number"
                  type="tel"
                  name="phone_number"
                  value={form.phone_number}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={fieldErrors.phone_number ? "error" : ""}
                  placeholder="Optional"
                />
                {fieldErrors.phone_number && (
                  <span className="field-error">{fieldErrors.phone_number}</span>
                )}
              </div>

              <div className="contactField">
                <label htmlFor="contact_method">Preferred contact method</label>
                <select
                  id="contact_method"
                  name="contact_method"
                  value={form.contact_method}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={fieldErrors.contact_method ? "error" : ""}
                >
                  <option value="">Select an option</option>
                  <option value="Email">Email</option>
                  <option value="Phone">Phone</option>
                </select>
                {fieldErrors.contact_method && (
                  <span className="field-error">{fieldErrors.contact_method}</span>
                )}
              </div>

              <div className="contactField contactField--full">
                <label htmlFor="area_of_interest">Area of interest</label>
                <select
                  id="area_of_interest"
                  name="area_of_interest"
                  value={form.area_of_interest}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={fieldErrors.area_of_interest ? "error" : ""}
                >
                  <option value="">Select a service area</option>
                  <option value="Collaboration Solutions">Collaboration Solutions</option>
                  <option value="Unified Communications as a Service">Unified Communications as a Service</option>
                  <option value="CybersecContact Center as a Serviceurity">Contact Center as a Service</option>
                  <option value="Security Solutions">Security Solutions</option>
                  <option value="Connectivity">Connectivity</option>
                  <option value="Hybrid Cloud Hosting">Hybrid Cloud Hosting</option>
                  <option value="Cloud Infrastructure">Cloud Infrastructure</option>
                  <option value="Data Center Services">Data Center Services</option>
                  <option value="Network Infrastructure">Network Infrastructure</option>
                  <option value="Wireless Infrastructure">Wireless Infrastructure</option>
                  <option value="Cabling Solutions">Cabling Solutions</option>
                  <option value="Cyber Security Infrastructure">Cyber Security Infrastructure</option>
                  <option value="Maritime Systems">Maritime Systems</option>
                  <option value="Intelligent Smart Cities Solutions">Intelligent Smart Cities Solutions</option>
                </select>
                {fieldErrors.area_of_interest && (
                  <span className="field-error">{fieldErrors.area_of_interest}</span>
                )}
              </div>

              <div className="contactField contactField--full">
                <label htmlFor="additional_comments">Project details</label>
                <textarea
                  id="additional_comments"
                  name="additional_comments"
                  value={form.additional_comments}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={fieldErrors.additional_comments ? "error" : ""}
                  placeholder="Tell us about your goals, challenges, timeline, or scope."
                  rows="6"
                />
                {fieldErrors.additional_comments && (
                  <span className="field-error">
                    {fieldErrors.additional_comments}
                  </span>
                )}
              </div>

              {success && (
                <div className="contact-success">
                  Thanks for reaching out. Our team will get back to you shortly.
                </div>
              )}

              {error && <div className="contact-error">{error}</div>}

              <button type="submit" className="contactSubmit" disabled={loading}>
                {loading ? "Sending..." : "Submit inquiry"}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
